import * as d3 from "d3"
import React, { useContext, useEffect, useRef, useState } from "react"
import { WebsocketContext } from "../../../../context/WebsocketContext"
import { isFacilitator } from "../../../../functions/FilterUsers"
import pencilIcon from "../../../../images/pencil-icon.svg"
import { useAppSelector } from "../../../../stores/hooks"
import { RootState } from "../../../../stores/store"
import CardEmpty from "./CardEmpty"
import CardLayout from "./layouts/CardLayout"
import { getRandomD3Ease, getRandomDurationValue, getRectColor, isColliding } from "./utils/PostitUtils"
import { isConstructorDeclaration } from "typescript"

interface IProps {
  cardData: ICard
  setCurrentCardFn: React.Dispatch<React.SetStateAction<ICard | undefined>>
  setDraggingFn: React.Dispatch<React.SetStateAction<boolean>>
  setShowEditCardModalFn: React.Dispatch<React.SetStateAction<boolean>>
  setShowConfirmDeleteModalFn: React.Dispatch<React.SetStateAction<boolean>>
}

const Postit = ({
  cardData,
  setCurrentCardFn,
  setDraggingFn,
  setShowEditCardModalFn,
  setShowConfirmDeleteModalFn,
}: IProps): React.JSX.Element => {
  const sessionStore = useAppSelector((state: RootState) => state.session)
  const canvasStore = useAppSelector((state: RootState) => state.canvas)
  const svgRef = useRef<SVGGElement | null>(null)
  const pencilRef = useRef<SVGGElement | null>(null)
  const emptySvgRef = useRef<SVGGElement | null>(null)
  const { send } = useContext(WebsocketContext)
  const [card, setCard] = useState<ICard>(cardData)
  const cardRef = useRef(card)
  const [rotation, setRotation] = useState(0)
  const lastPos = useRef<{ x: number; y: number; t: number }>({ x: cardData.x ?? 0, y: cardData.y ?? 0, t: Date.now() })
  const [dragging, setDragging] = useState(false)
  const [showPencil, setShowPencil] = useState(false)
  const [showPencilItSelf, setShowPencilItself] = useState(false)
  const wasJustCancelled = useRef(false)
  const isFeedbackEditable = canvasStore.feedback?.status !== "completed"

  useEffect(() => {
    cardRef.current = card
  }, [card])

  useEffect(() => {
    setCard(cardData)
  }, [cardData])

  const updateCardInitialPosition = (event: d3.D3DragEvent<SVGGElement, ICard, unknown>) => {
    const centerX = event.x - 50
    const centerY = event.y - 50
    return { ...cardRef.current, initial_x: centerX, initial_y: centerY }
  }

  const isCardOwnedByCurrentUser = () => card.owner === sessionStore.id
  const isCardSquare = () => card.shape === "square"

  const setDelayedHiding = () => {
    setTimeout(() => {
      setShowPencil(false)
    }, 2000)
  }

  const showCard = (): boolean => {
    const specialCategories = ["roles", "activities", "expected-results"]
    if (card.category && specialCategories.includes(card.category)) {
      return true
    }

    return (
      canvasStore.feedback?.status === "completed" ||
      canvasStore.feedback?.visible === true ||
      isFacilitator(canvasStore.feedback?.users ?? [], sessionStore.email) ||
      isCardOwnedByCurrentUser()
    )
  }

  useEffect(() => {
    if (!canEditCard()) return

    if (svgRef.current) {
      const svg = d3.select(svgRef.current) as d3.Selection<SVGGElement, ICard, null, undefined>

      svg.call(
        d3
          .drag<SVGGElement, ICard>()
          .on("start", function (event, _d) {
            const cardElement = document.getElementById(`card-${card.id}`)
            if (cardElement && cardElement.parentNode) {
              cardElement.parentNode.appendChild(cardElement)
            }

            d3.select<SVGGElement, ICard>(this).attr("cursor", "grabbing")
            setCurrentCardFn(cardRef.current)
            lastPos.current = { x: event.x, y: event.y, t: Date.now() }
            setRotation(0)

            if (wasJustCancelled.current) {
              wasJustCancelled.current = false
            } else {
              const updatedCard = updateCardInitialPosition(event)
              setCard(updatedCard)
            }
          })
          .on("drag", function (event, _d) {
            const prevCard = cardRef.current
            const updatedCard = {
              ...prevCard,
              x: (prevCard.x ?? 0) + event.dx,
              y: (prevCard.y ?? 0) + event.dy,
            }

            setShowPencil(false)
            setDraggingFn(true)
            setDragging(true)
            setCard(updatedCard)

            const now = Date.now()
            const dx = event.x - lastPos.current.x
            const angle = Math.max(-15, Math.min(15, dx * 0.9))
            setRotation(angle)
            lastPos.current = { x: event.x, y: event.y, t: now }
          })
          .on("end", function (_event, _d) {
            d3.select<SVGGElement, ICard>(this).attr("cursor", "grab")

            let colliderTarget: SVGGElement | undefined
            const originElement = d3.select<SVGGElement, ICard>(`#hitbox-${card.id}`)

            setShowPencil(true)

            if (isTrashTarget(cardRef.current)) {
              setShowConfirmDeleteModalFn(true)

              wasJustCancelled.current = true

              const updated = {
                ...cardRef.current,
                x: cardRef.current.initial_x ?? 0,
                y: cardRef.current.initial_y ?? 0,
              }
              setCard(updated)
              cardRef.current = updated
              setDragging(false)
              setDraggingFn(false)
              return
            }

            d3.selectAll(".droppable-target").each(function () {
              if (isColliding(originElement.node() as SVGGElement, this as SVGGElement)) {
                colliderTarget = this as SVGGElement
              }
            })

            const category = colliderTarget?.id
            const updatedCard = {
              ...cardRef.current,
              category,
              color: getRectColor(card),
              last_dragged_by: sessionStore.id,
            }

            setCard(updatedCard)
            cardRef.current = updatedCard
            send("editcard", updatedCard)

            setDragging(false)
            setDraggingFn(false)

            let frame: number
            const animateBack = () => {
              setRotation((prev) => {
                if (Math.abs(prev) < 1) return 0
                return prev * 0.1
              })
              if (Math.abs(rotation) > 1) {
                frame = requestAnimationFrame(animateBack)
              } else {
                setRotation(0)
              }
            }
            animateBack()
            return () => cancelAnimationFrame(frame)
          })
      )
    }
  }, [])

  const isTrashTarget = (card: ICard): boolean => {
    const originElement = d3.select<SVGGElement, ICard>(`#hitbox-${card.id}`)
    let isTrashTarget = false

    d3.selectAll(".trash-target").each(function () {
      if (isColliding(originElement.node() as SVGGElement, this as SVGGElement)) {
        isTrashTarget = true
      }
    })

    return isTrashTarget
  }

  const getCurrentCardText = (card: ICard): string => {
    const foundCard = canvasStore.cards.find((c) => c.id === card.id)
    if (foundCard && foundCard.text !== card.text) {
      setCard({ ...card, text: foundCard.text })
    }
    return foundCard?.text ?? ""
  }

  useEffect(() => {
    const isCardOwner = card.owner === sessionStore.id
    const lastDraggedByFacilitator = isFacilitator(canvasStore.feedback?.users ?? [], card.last_dragged_by ?? "")

    const shouldAnimate = card.last_dragged_by !== sessionStore.id &&
      !(isCardOwner && lastDraggedByFacilitator)

    if (shouldAnimate && !dragging) {
      const g = d3.select(svgRef.current)
      console.log("Passei aqui 1", shouldAnimate)

      animateCardTransition(g)

      if (!showCard()) {
        const g = d3.select(emptySvgRef.current)
        g.attr("transform", `translate(${card.initial_x ?? card.x}, ${card.initial_y ?? card.y}) rotate(${rotation}, 50, 50)`)
        g.transition()
          .duration(getRandomDurationValue())
          .ease(getRandomD3Ease())
          .attr("transform", `translate(${card.x}, ${card.y}) rotate(${rotation}, 50, 50)`)
      }
    }
  }, [card.x, card.y, card.last_dragged_by, showCard(), dragging])

  const animateCardTransition = (element: d3.Selection<SVGGElement | null, unknown, null, undefined>) => {
    element.attr("transform", `translate(${card.initial_x ?? card.x}, ${card.initial_y ?? card.y}) rotate(${rotation}, 50, 50)`)
    element.transition()
      .duration(getRandomDurationValue())
      .ease(getRandomD3Ease())
      .attr("transform", `translate(${card.x}, ${card.y}) rotate(${rotation}, 50, 50)`)
  }

  const canEditCard = (): boolean => {
    return isFeedbackEditable && (isCardOwnedByCurrentUser() || (isFacilitator(canvasStore.feedback?.users ?? [], sessionStore.email) ?? false))
  }

  const cardMouseOver = () => {
    setShowPencil(true)
  }

  const cardMouseOut = () => {
    setDelayedHiding()
  }

  const pencilMouseOver = () => {
    setShowPencilItself(true)
  }

  const pencilMouseOut = () => {
    setShowPencilItself(false)
  }

  function shouldRenderEditButton() {
    return (showPencil || showPencilItSelf) && isCardSquare() && canEditCard()
  }

  return (
    <g id={`card-${card.id}`}>
      <defs>
        <filter id="drop-shadow" height="130%">
          <feDropShadow dx="0" dy="4" stdDeviation="4" floodColor="#000" floodOpacity="0.3" />
        </filter>
      </defs>
      <g
        ref={emptySvgRef}
        id={`group-${card.id}-empty`}
        data-motion-hash={card.motion_hash}
        transform={`translate(${card.x}, ${card.y}) rotate(${rotation}, 50, 50)`}
        width="100"
        height="100"
        style={{
          display: isCardSquare() && !showCard() ? undefined : "none",
        }}
        filter={dragging ? "url(#drop-shadow)" : "none"}
      >
        {!showCard() && <CardEmpty text={getCurrentCardText(card)} card={card} />}
      </g>
      <g
        ref={svgRef}
        id={`group-${card.id}`}
        data-motion-hash={card.motion_hash}
        transform={`translate(${card.x}, ${card.y}) rotate(${rotation}, 50, 50)`}
        width="100"
        height="100"
        style={{
          display: showCard() ? undefined : "none",
          pointerEvents: showCard() ? "auto" : "none",
        }}
        filter={dragging ? "url(#drop-shadow)" : undefined}
      >
        <CardLayout card={card} currentCardText={getCurrentCardText(card)} />
        {isCardSquare() && canEditCard() && (
          <rect
            id={`over-${card.id}`}
            width="100"
            height="100"
            fill="transparent"
            onMouseOver={cardMouseOver}
            onMouseOut={cardMouseOut}
          ></rect>
        )}
      </g>
      {shouldRenderEditButton() && (
        <g
          ref={pencilRef}
          id={`card-pencil-btn-${card.id}`}
          className="edit-pencil-btn"
          style={{ cursor: "pointer" }}
          transform={`translate(${(card.x ?? 0) + 99}, ${(card.y ?? 0) + 15}) rotate(${rotation}, 50, 50)`}
          onMouseOver={pencilMouseOver}
          onMouseOut={pencilMouseOut}
          onClick={(e) => {
            e.stopPropagation()
            setCurrentCardFn(cardRef.current)
            setShowEditCardModalFn(true)
          }}
        >
          <image href={pencilIcon} x="2" y="2" width="16" height="16" />
        </g>
      )}
    </g>
  )
}

export default Postit
